import React, { Component } from 'react';
import classNames from 'classnames';
import get from 'lodash/get';
import xor from 'lodash/xor';
import produce from 'immer';
import { savePaymentConfiguration as savePaymentConfigurationClevertap } from 'clevertap/paymentConfiguration';
import styles from './PaymentConfigurationForm.css';
import PaymentMethodChecklist from './PaymentMethodChecklist';
import PaymentConfigurationFooter from './PaymentConfigurationFooter';
import OutletAssignmentModal from '../OutletAssignmentModal';
import RemoveConfigurationModal from '../RemoveConfigurationModal';

class PaymentConfigurationForm extends Component {
  state = {
    changes: {},
    isAssigningOutlets: false,
    showRemoveConfigurationModal: false,
    changesBeforeSave: {},
  };

  componentDidUpdate(prevProps, prevState) {
    const { onChange, errorMessage } = this.props;
    const { changes, changesBeforeSave } = this.state;

    if (prevState.changes !== changes) {
      onChange(changes);
    }

    if (prevProps.errorMessage !== errorMessage) {
      if (errorMessage) {
        this.setState({ changes: changesBeforeSave });
      } else {
        this.setState({ changesBeforeSave: {} });
      }
    }
  }

  componentDidMount() {
    this.props.onLoad();
  }

  handleNameFieldChange = event => {
    const { configuration } = this.props;
    const { changes } = this.state;
    const originalName = get(configuration, 'name');
    const newName = event.target.value.trimStart();

    this.setState({
      changes: produce(changes, draft => {
        if (newName === originalName) {
          delete draft.name;
        } else {
          draft.name = newName;
        }
      }),
    });
  };

  handlePaymentMethodChange = selectedPaymentMethods => {
    const { configuration } = this.props;
    const { changes } = this.state;
    const originalSelections = get(configuration, 'paymentMethods', []).map(
      ({ id }) => id
    );

    this.setState({
      changes: produce(changes, draft => {
        if (xor(originalSelections, selectedPaymentMethods).length < 1) {
          delete draft.paymentMethodIds;
        } else {
          draft.paymentMethodIds = selectedPaymentMethods;
        }
      }),
    });
  };

  handleOutletButtonClick = () => {
    this.setState({
      isAssigningOutlets: true,
    });
  };

  handleOutletAssignmentCancel = () => {
    this.setState({
      isAssigningOutlets: false,
    });
  };

  handleOutletAssignmentConfirm = outletIds => {
    const { configuration } = this.props;
    const { changes } = this.state;
    const originalSelections = get(configuration, 'outletIds');

    this.setState({
      isAssigningOutlets: false,
      changes: produce(changes, draft => {
        if (xor(originalSelections, outletIds).length < 1) {
          delete draft.outletIds;
        } else {
          draft.outletIds = outletIds;
        }
      }),
    });
  };

  handleRemoveConfiguration = () => {
    const { configuration, onClose, deleteConfiguration } = this.props;
    deleteConfiguration(configuration.id);
    this.setState({ showRemoveConfigurationModal: false });
    onClose();
  };

  handleRemoveConfigurationCancel = () => {
    this.setState({ showRemoveConfigurationModal: false });
  };

  handleDeleteClick = () => {
    const { configuration, onClose, deleteConfiguration } = this.props;
    if (configuration.outletIds.length > 0) {
      this.setState({ showRemoveConfigurationModal: true });
    } else {
      deleteConfiguration(configuration.id);
      onClose();
    }
  };

  handleSaveConfiguration = () => {
    const {
      paymentMethods,
      isNew,
      createNewConfiguration,
      updateConfiguration,
      configuration,
    } = this.props;
    const { changes } = this.state;

    const fields = {
      name: '',
      outletIds: [],
      paymentMethodIds: [],
      ...configuration,
      ...changes,
    };

    const selectedPaymentMethods = fields.paymentMethodIds.map(id =>
      paymentMethods.find(paymentMethod => paymentMethod.id === id)
    );

    const displaySelectedMethod = selectedPaymentMethods.map(
      payment => `${payment.category.name} - ${payment.name}`
    );

    const properties = {
      name: fields.name,
      outletCount: fields.outletIds.length,
      paymentMethods: displaySelectedMethod,
    };

    this.setState({ changesBeforeSave: changes });

    if (isNew) {
      const defaultSelectedPaymentMethodIds = paymentMethods
        .filter(method => method.isMokaSetEnabled)
        .map(({ id }) => id);
      if (changes.name) {
        const newConfiguration = {
          paymentMethodIds: defaultSelectedPaymentMethodIds,
          ...changes,
        };
        createNewConfiguration(newConfiguration);
      }
    } else {
      updateConfiguration({ ...configuration, ...changes });
    }
    this.setState({ changes: {} });

    savePaymentConfigurationClevertap(properties);
  };

  render() {
    const {
      isLoading,
      configuration,
      paymentMethods,
      isNew,
      onClose,
      paymentMethodCategories,
      errorMessage,
    } = this.props;
    const {
      changes,
      isAssigningOutlets,
      showRemoveConfigurationModal,
    } = this.state;

    if (isLoading) {
      return null;
    }

    // For new creation, we select all with `isMokaSetEnabled: true`
    const selectedPaymentMethodIds = isNew
      ? paymentMethods
          .filter(method => method.isMokaSetEnabled)
          .map(({ id }) => id)
      : [];

    const fields = {
      name: '',
      outletIds: [],
      paymentMethodIds: selectedPaymentMethodIds,
      ...configuration,
      ...changes,
    };

    // Default configuration cannot be edited. But we can assign outlets.
    const isDefaultConfig = get(configuration, 'isDefaultConfiguration', false);
    const isUneditable = isDefaultConfig;
    const hasChanges = Object.keys(changes).length > 0;
    const originalName = get(configuration, 'name', '');

    const nameHasChanged = originalName !== fields.name;

    // Only alphabets, digits and whitespace is allowed
    const regex = /[^A-Za-z0-9 ]/g;
    const hasInvalidCharacter = regex.test(fields.name);

    const isNameValid = !hasInvalidCharacter && fields.name;

    return (
      <div className={styles.container}>
        <div className={styles.form}>
          <div className={styles.sectionHeader}>Basic Information</div>

          <div className={styles.sectionBody}>
            <div
              className={classNames(
                'paycon-ftue-form-name',
                'form-group',
                styles.formRow,
                {
                  'has-success': isNameValid && nameHasChanged,
                  'has-error': !isNameValid && nameHasChanged,
                }
              )}
            >
              <label className={styles.formLabel}>Configuration Name</label>
              <div className={styles.inputWithIcon}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Configuration 1"
                  value={fields.name}
                  onChange={this.handleNameFieldChange}
                  disabled={isUneditable}
                />
                {nameHasChanged && (
                  <i
                    className={classNames(
                      styles.inputIcon,
                      'glyphicon',
                      isNameValid && [styles.glyphiconOk, 'glyphicon-ok'],
                      !isNameValid && [
                        styles.glyphiconRemove,
                        'glyphicon-remove',
                      ]
                    )}
                  />
                )}
              </div>

              {errorMessage && (
                <small className={styles.nameError}>{errorMessage}</small>
              )}
            </div>

            <div
              className={classNames(styles.formRow, 'paycon-ftue-form-outlets')}
            >
              <label className={styles.formLabel}>Assigned Outlets</label>
              <button
                type="button"
                className={classNames('btn btn-default', styles.outletButton)}
                onClick={this.handleOutletButtonClick}
                disabled={isUneditable || isAssigningOutlets}
              >
                <div>{fields.outletIds.length} outlets</div>
                <i className="fa fa-chevron-right" />
              </button>
            </div>
          </div>

          <div
            className={classNames(
              styles.sectionHeader,
              'paycon-ftue-checklist-section'
            )}
          >
            Configuration
          </div>
          <div
            className={classNames(
              styles.sectionBody,
              'paycon-ftue-checklist-section'
            )}
          >
            <div className={styles.formRow}>
              <small>At least one payment method must be enabled</small>
            </div>

            <PaymentMethodChecklist
              paymentMethods={paymentMethods}
              selectedIds={fields.paymentMethodIds}
              onChange={this.handlePaymentMethodChange}
              disabled={isUneditable}
              paymentMethodCategories={paymentMethodCategories}
            />
          </div>
        </div>

        <PaymentConfigurationFooter
          onCancelClick={onClose}
          onSaveClick={this.handleSaveConfiguration}
          canDelete={!isDefaultConfig}
          canSubmit={hasChanges && isNameValid}
          onDeleteClick={this.handleDeleteClick}
          paymentMethods={paymentMethods}
          selectedIds={fields.paymentMethodIds}
        />

        {isAssigningOutlets && (
          <OutletAssignmentModal
            isShowing
            configurationName={fields.name}
            selectedIds={fields.outletIds}
            onCancel={this.handleOutletAssignmentCancel}
            onConfirm={this.handleOutletAssignmentConfirm}
          />
        )}

        {showRemoveConfigurationModal && (
          <RemoveConfigurationModal
            isShowing
            onCancel={this.handleRemoveConfigurationCancel}
            onRemove={this.handleRemoveConfiguration}
          />
        )}
      </div>
    );
  }
}

PaymentConfigurationForm.defaultProps = {
  configuration: null,
  paymentMethods: [],
  onChange: () => {},
  onClose: () => {},
  isNameValid: true,
};

export default PaymentConfigurationForm;
